import React, { useState, useEffect, useMemo } from 'react';
import { ExpenseService } from '../services/api';
import Modal from './common/Modal';
import { useSettings } from '../context/SettingsContext';
import { getTerminology } from '../utils/terminology';
import Swal from 'sweetalert2';

import { exportToExcel } from '../utils/exportHelper';

const ExpenseList = () => {
  const { settings } = useSettings();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [saving, setSaving] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.role || user?.data?.role;
  const canExport = userRole === 'admin' || userRole === 'super_admin';
  const terminology = getTerminology(user?.school?.type || user?.data?.school?.type);

  // Stats
  const [stats, setStats] = useState({ total_ytd: 0, this_month: 0, pending: 0 });

  const [editingId, setEditingId] = useState(null);
  const defaultFormData = {
    title: '',
    category: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  };
  const [formData, setFormData] = useState(defaultFormData);

  // Categories State
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('expense_categories');
    return saved ? JSON.parse(saved) : ['Rent', 'Utility', 'Maintenance', 'Refreshment', 'Salary', 'Other'];
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  // Save categories whenever they change
  useEffect(() => {
    localStorage.setItem('expense_categories', JSON.stringify(categories));
  }, [categories]);

  const getCurrencySymbol = (code) => {
    switch (code) {
      case 'PKR': return 'Rs. ';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'INR': return '₹';
      case 'AED': return 'AED ';
      case 'USD': default: return '$';
    }
  };

  const handleAddCategory = (e) => {
    e.preventDefault();
    if (newCategory && !categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
      setNewCategory('');
    }
  };

  const handleDeleteCategory = (cat) => {
    setCategories(categories.filter(c => c !== cat));
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Rent': 'bg-primary-subtle text-primary border-primary',
      'Utility': 'bg-warning-subtle text-warning border-warning',
      'Maintenance': 'bg-danger-subtle text-danger border-danger',
      'Refreshment': 'bg-info-subtle text-info border-info',
      'Salary': 'bg-success-subtle text-success border-success',
      'Other': 'bg-secondary-subtle text-secondary border-secondary'
    };
    return colors[category] || 'bg-light text-dark border-secondary-subtle';
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      const response = await ExpenseService.getAll();
      const data = response.data;
      setExpenses(data);
      calculateStats(data);
    } catch (error) {
      console.error("Error loading expenses", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const total_ytd = data.reduce((acc, curr) => {
      const expDate = new Date(curr.date);
      if (expDate.getFullYear() === currentYear) {
        return acc + (parseFloat(curr.amount) || 0);
      }
      return acc;
    }, 0);

    const this_month = data.reduce((acc, curr) => {
      const expDate = new Date(curr.date);
      if (expDate.getFullYear() === currentYear && expDate.getMonth() === currentMonth) {
        return acc + (parseFloat(curr.amount) || 0);
      }
      return acc;
    }, 0);

    setStats({ total_ytd, this_month });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editingId) {
        await ExpenseService.update(editingId, formData);
        Swal.fire({ icon: 'success', title: 'Success', text: 'Expense updated successfully!', timer: 1500, showConfirmButton: false });
      } else {
        await ExpenseService.create(formData);
        Swal.fire({ icon: 'success', title: 'Success', text: 'Expense added successfully!', timer: 1500, showConfirmButton: false });
      }
      setShowModal(false);
      loadExpenses();
      setEditingId(null);
      setFormData(defaultFormData);
    } catch (error) {
      console.error("Error saving expense", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingId(expense.id);
    setFormData({
      title: expense.title || '',
      category: expense.category || '',
      amount: expense.amount || '',
      date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      description: expense.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await ExpenseService.delete(id);
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Expense has been deleted.', timer: 1500, showConfirmButton: false });
        loadExpenses();
      } catch (error) {
        console.error("Error deleting expense", error);
      }
    }
  };

  const openNewModal = () => {
    setEditingId(null);
    setFormData(defaultFormData);
    setShowModal(true);
  };

  // Filter Logic
  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const matchesSearch = expense.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || expense.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [expenses, searchTerm, categoryFilter]);

  const handleExport = () => {
    if (filteredExpenses.length === 0) {
      Swal.fire('No Data', 'No expense records to export.', 'info');
      return;
    }

    const exportData = filteredExpenses.map(expense => ({
      'Title': expense.title,
      'Category': expense.category,
      'Amount': expense.amount,
      'Currency': settings.currency || 'PKR',
      'Date': expense.date ? new Date(expense.date).toLocaleDateString() : '-',
      'Description': expense.description || '-'
    }));

    exportToExcel(exportData, `Expenses_Export_${new Date().toISOString().split('T')[0]}`);
  };


  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="fw-bold">Expense Manager</h2>
          <p className="text-muted">Track and analyze {terminology.institution.toLowerCase()} spending</p>
        </div>
        <div className="d-flex gap-2">
          {canExport && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-3 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          <button className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-3 shadow-sm" onClick={() => setShowCategoryModal(true)}>
            <i className="bi bi-tags"></i> Manage Categories
          </button>
          <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={openNewModal}>
            <i className="bi bi-plus-lg"></i> Add Expense
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Total Spent (YTD)</p>
            <h3 className="mb-0 text-dark">{getCurrencySymbol(settings?.currency)}{stats.total_ytd.toLocaleString()}</h3>
          </div>
          <div className="stat-icon bg-red-light">
            <i className="bi bi-graph-down-arrow"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">This Month</p>
            <h3 className="mb-0 text-dark">{getCurrencySymbol(settings?.currency)}{stats.this_month.toLocaleString(undefined, { maximumFractionDigits: 0 })}</h3>
          </div>
          <div className="stat-icon bg-orange-light">
            <i className="bi bi-calendar-event"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Categories</p>
            <h3 className="mb-0">{categories.length}</h3>
          </div>
          <div className="stat-icon bg-blue-light">
            <i className="bi bi-tags-fill"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">
        <div className="section-header d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          <h5 className="mb-0 fw-bold">Transaction History</h5>

          <div className="d-flex gap-3 align-items-center flex-grow-1 justify-content-end">
            {/* Category Filter */}
            <select
              className="form-select rounded-pill bg-light border-0"
              style={{ width: '150px' }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="All">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>

            {/* Search */}
            <div className="position-relative">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '220px', height: '40px' }}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">Title / Description</th>
                <th>Category</th>
                <th>Date</th>
                <th>Amount</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-5 text-muted">Loading transactions...</td></tr>
              ) : filteredExpenses.length > 0 ? (
                filteredExpenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-sm bg-light text-primary d-flex align-items-center justify-content-center rounded-circle" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-receipt"></i>
                        </div>
                        <div>
                          <div className="fw-bold text-dark">{expense.title}</div>
                          <small className="text-muted text-truncate d-inline-block" style={{ maxWidth: '200px' }}>{expense.description}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge border px-3 py-2 rounded-pill fw-normal ${getCategoryColor(expense.category)}`}>
                        {expense.category}
                      </span>
                    </td>
                    <td><span className="text-muted"><i className="bi bi-calendar3 me-2"></i>{new Date(expense.date).toLocaleDateString()}</span></td>
                    <td><span className="fw-bold text-dark fs-6">{getCurrencySymbol(settings?.currency)}{expense.amount}</span></td>
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-icon btn-light rounded-circle text-warning shadow-sm d-flex align-items-center justify-content-center p-0" style={{ width: 35, height: 35 }} title="Edit" onClick={() => handleEdit(expense)}>
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <button className="btn btn-icon btn-light rounded-circle text-danger shadow-sm d-flex align-items-center justify-content-center p-0" style={{ width: 35, height: 35 }} title="Delete" onClick={() => handleDelete(expense.id)}>
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" className="text-center py-5 text-muted">No expenses found matching your criteria.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)} title={editingId ? "Edit Expense" : "Log New Expense"}>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Expense Title</label>
            <input type="text" className="form-control" name="title" value={formData.title} onChange={handleInputChange} required placeholder="e.g. Office Rents" />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Category</label>
              <select className="form-select" name="category" value={formData.category} onChange={handleInputChange} required>
                <option value="">Select Category</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Amount ({getCurrencySymbol(settings?.currency)})</label>
              <input type="number" className="form-control" name="amount" value={formData.amount} onChange={handleInputChange} required />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Date</label>
            <input type="date" className="form-control" name="date" value={formData.date} onChange={handleInputChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Description (Optional)</label>
            <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows="3" />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4 pt-4 border-top">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : (editingId ? 'Update' : 'Save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Category Management Modal */}
      <Modal show={showCategoryModal} onClose={() => setShowCategoryModal(false)} title="Manage Expense Categories">
        <div className="mb-4">
          <form onSubmit={handleAddCategory} className="d-flex gap-2">
            <input
              type="text"
              className="form-control rounded-pill"
              placeholder="New Category Name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              required
            />
            <button type="submit" className="btn btn-primary rounded-pill px-4">Add</button>
          </form>
        </div>

        <div className="list-group list-group-flush border rounded-3 overflow-hidden">
          {categories.map((cat, index) => (
            <div key={index} className="list-group-item d-flex justify-content-between align-items-center bg-transparent">
              <span className="fw-medium">{cat}</span>
              <button
                className="btn btn-sm btn-icon btn-light text-danger rounded-circle shadow-sm"
                onClick={() => handleDeleteCategory(cat)}
                title="Delete Category"
                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
          {categories.length === 0 && (
            <div className="p-4 text-center text-muted">No categories found. Add one above.</div>
          )}
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowCategoryModal(false)}>Close</button>
        </div>
      </Modal>
    </div>
  );
};

export default ExpenseList;
